// skills/farming/farming.js
// Farming skill logic - passive plot growth + active raking

import { GRASS_PATCH, CROPS, HERBS, TREES, TOOLS, FARM_CONFIG } from './farmingData.js';
import { gameState } from '../../game/gameState.js';
import { addXP, applyMasteryToMultiplier } from '../../systems/experience.js';
import { SKILLS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { isBloomholdEligible, depositToBloomhold } from '../../systems/bloomhold.js';
import { showItemGain } from '../../ui/floatingNotifications.js';
import { getTalentFlatBonus, getTalentLootBonus, getTalentProcChance, rollTalentProc } from '../../systems/talentTree/talentTree.js';
import { architectingSkill } from '../architecting/architecting.js';
import { recordTalentProc } from '../../ui/talentActivity.js';
import { BaseSkill } from '../../core/BaseSkill.js';
import { bountySkill } from '../bounty/bounty.js';

class FarmingSkill extends BaseSkill {
    constructor() {
        super(SKILLS.FARMING, TOOLS, 'WOODEN');
    }

    /**
     * Get the currently equipped tool object (alias for BaseSkill method)
     */
    get currentTool() {
        return this.getCurrentTool();
    }

    /**
     * Get the active loot multiplier from highest owned tool
     * Applies mastery bonus (doubles tool effect at level 120)
     * Note: Farming tools provide loot bonuses, not speed bonuses
     */
    getLootMultiplier() {
        const activeTool = this.getActiveBuff();
        const baseLootMultiplier = activeTool.lootMultiplier || 1.0;
        return applyMasteryToMultiplier(SKILLS.FARMING, baseLootMultiplier);
    }

    // ==================== RAKING (Active Action) ====================

    // Check if player can rake grass (always true if level 1+)
    canRake() {
        return { success: true };
    }

    // Start raking the grass patch for seeds
    startRaking() {
        // Raking uses fixed time (tools provide loot bonuses for harvests, not speed)
        const rakingTime = GRASS_PATCH.baseTime;

        const action = {
            type: 'farming',
            subType: 'raking',
            name: `Raking ${GRASS_PATCH.name}`,
            patch: GRASS_PATCH,
            patchId: 'GRASS_PATCH',
            progress: 0,
            duration: rakingTime,
            startTime: Date.now(),
            forceComplete: false,

            tick: (speedMultiplier = 1) => {
                if (action.forceComplete) {
                    action.forceComplete = false;
                    this.completeRaking();
                    const canContinue = this.canRake();
                    if (canContinue.success) {
                        this.startRaking();
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                if (action.progress >= 1) {
                    this.completeRaking();
                    const canContinue = this.canRake();
                    if (canContinue.success) {
                        this.startRaking();
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        return true;
    }

    // Complete raking - awards XP and possibly drops seeds
    completeRaking() {
        const xpResult = addXP(SKILLS.FARMING, GRASS_PATCH.xp, GRASS_PATCH.id);

        // Get seed drop bonus from talents (increases drop chance)
        const seedDropBonus = getTalentFlatBonus(SKILLS.FARMING, 'seedDropBonus');

        // Roll for seed drops (base 30% + talent bonus)
        const roll = Math.random() * 100;
        const baseWeight = GRASS_PATCH.drops.reduce((sum, d) => sum + d.weight, 0);
        // Add bonus to threshold (e.g., 30% + 30% bonus = 39% total)
        const totalWeight = baseWeight * (1 + seedDropBonus);

        if (roll < totalWeight) {
            // Determine which seed (use base weights for distribution)
            let cumulative = 0;
            const scaledRoll = roll * (baseWeight / totalWeight); // Scale roll back to base distribution
            for (const drop of GRASS_PATCH.drops) {
                cumulative += drop.weight;
                if (scaledRoll < cumulative) {
                    gameState.addItem(drop.itemId, drop.quantity);
                    showItemGain(drop.itemId, drop.quantity);
                    logger.game(`Found ${drop.quantity} ${drop.itemId}!`);
                    break;
                }
            }
        }

        this.onRakingComplete(xpResult);
    }

    stopRaking() {
        const action = gameState.getCurrentAction();
        if (action && action.type === 'farming' && action.subType === 'raking') {
            gameState.clearAction();
        }
    }

    // ==================== PLOT MANAGEMENT (Passive Growth) ====================

    /**
     * Check if a plot is unlocked based on player level or admin override
     */
    isPlotUnlocked(plotIndex) {
        // Check if plot was manually unlocked (e.g., by admin panel)
        const plot = gameState.getFarmPlot(plotIndex);
        if (plot && plot.unlocked) {
            return true;
        }

        // Otherwise check level requirement
        const skill = gameState.getSkill(SKILLS.FARMING);
        const requiredLevel = FARM_CONFIG.UNLOCK_LEVELS[plotIndex];
        return requiredLevel !== undefined && skill.level >= requiredLevel;
    }

    /**
     * Check if auto-harvest is unlocked
     */
    isAutoHarvestUnlocked() {
        const skill = gameState.getSkill(SKILLS.FARMING);
        return skill.level >= FARM_CONFIG.AUTO_HARVEST_UNLOCK_LEVEL;
    }

    /**
     * Get crop, herb, or tree data by type and id
     */
    getCropData(cropType, cropId) {
        if (cropType === 'crop') {
            return CROPS[cropId.toUpperCase()];
        } else if (cropType === 'herb') {
            return HERBS[cropId.toUpperCase()];
        } else if (cropType === 'tree') {
            return TREES[cropId.toUpperCase()];
        }
        return null;
    }

    /**
     * Check if player can plant a specific crop/tree
     * Returns seedSource: 'inventory' | 'bloomhold' to indicate where seeds are
     */
    canPlant(cropType, cropId) {
        const cropData = this.getCropData(cropType, cropId);
        if (!cropData) {
            return { success: false, reason: 'Invalid crop/tree' };
        }

        const skill = gameState.getSkill(SKILLS.FARMING);
        if (skill.level < cropData.level) {
            return { success: false, reason: `Requires level ${cropData.level} Farming` };
        }

        // Check for seeds - inventory first, then BloomHold
        const inventoryCount = gameState.getItemCount(cropData.seedId);
        if (inventoryCount >= cropData.seedCost) {
            return { success: true, cropData, seedSource: 'inventory' };
        }

        // Check BloomHold if unlocked
        if (gameState.isBloomholdUnlocked()) {
            const bloomholdCount = gameState.getBloomholdItemCount(cropData.seedId);
            if (bloomholdCount >= cropData.seedCost) {
                return { success: true, cropData, seedSource: 'bloomhold' };
            }
            // Show combined count in error message
            const totalCount = inventoryCount + bloomholdCount;
            if (totalCount < cropData.seedCost) {
                return { success: false, reason: `Need ${cropData.seedCost} ${cropData.seedId.replace(/_/g, ' ')}s (have ${totalCount})` };
            }
        }

        return { success: false, reason: `Need ${cropData.seedCost} ${cropData.seedId.replace(/_/g, ' ')}s (have ${inventoryCount})` };
    }

    /**
     * Calculate effective growth time with all talent reductions
     */
    getEffectiveGrowthTime(cropData, cropType) {
        let growthTime = cropData.growthTime;

        // Apply base growth time reduction talent
        const baseReduction = getTalentFlatBonus(SKILLS.FARMING, 'growthTimeReduction');
        if (baseReduction > 0) {
            growthTime *= (1 - baseReduction);
        }

        // Apply verdant blessing reduction (capstone)
        const verdantReduction = getTalentFlatBonus(SKILLS.FARMING, 'verdantGrowthReduction');
        if (verdantReduction > 0) {
            growthTime *= (1 - verdantReduction);
        }

        // Apply tree-specific reduction
        if (cropType === 'tree') {
            const treeReduction = getTalentFlatBonus(SKILLS.FARMING, 'treeGrowthReduction');
            if (treeReduction > 0) {
                growthTime *= (1 - treeReduction);
            }
        }

        // Apply fastGrowth proc (50% faster)
        if (rollTalentProc(SKILLS.FARMING, 'fastGrowth')) {
            growthTime *= 0.5;
            recordTalentProc(SKILLS.FARMING, "Nature's Haste", 'Growth time halved!', null);
        }

        return Math.floor(growthTime);
    }

    /**
     * Plant in a specific plot
     */
    plantInPlot(plotIndex, cropType, cropId) {
        const plot = gameState.getFarmPlot(plotIndex);
        if (!plot) {
            return { success: false, reason: 'Invalid plot' };
        }

        if (!this.isPlotUnlocked(plotIndex)) {
            return { success: false, reason: `Plot locked - requires level ${FARM_CONFIG.UNLOCK_LEVELS[plotIndex]}` };
        }

        if (plot.state !== 'empty') {
            return { success: false, reason: 'Plot is not empty' };
        }

        const canPlantResult = this.canPlant(cropType, cropId);
        if (!canPlantResult.success) {
            return canPlantResult;
        }

        const cropData = canPlantResult.cropData;
        const seedSource = canPlantResult.seedSource;

        // Check for freeSeed proc (Eternal Garden capstone)
        const freeSeed = rollTalentProc(SKILLS.FARMING, 'freeSeed');
        if (freeSeed) {
            recordTalentProc(SKILLS.FARMING, 'Eternal Garden', 'Seed preserved!', null);
        }

        // Consume seeds from the appropriate source (unless freeSeed procced)
        if (!freeSeed) {
            if (seedSource === 'bloomhold') {
                gameState.removeBloomholdItem(cropData.seedId, cropData.seedCost);

                // Award Architecting XP from Realm Steward talent (if unlocked)
                architectingSkill.awardBloomholdXP();

                logger.game(`Planted ${cropData.name} in plot ${plotIndex + 1} (seed from BloomHold)`);
            } else {
                gameState.removeItem(cropData.seedId, cropData.seedCost);
                logger.game(`Planted ${cropData.name} in plot ${plotIndex + 1}`);
            }
        } else {
            logger.game(`Planted ${cropData.name} in plot ${plotIndex + 1} (free seed!)`);
        }

        // Calculate growth time with talent reductions
        const effectiveGrowthTime = this.getEffectiveGrowthTime(cropData, cropType);

        // Check for instant growth proc (Verdant Blessing capstone)
        const instantGrowth = rollTalentProc(SKILLS.FARMING, 'instantGrowth');
        if (instantGrowth) {
            recordTalentProc(SKILLS.FARMING, 'Verdant Blessing', 'Instant growth!', null);
            // Plant and immediately mark as ready
            gameState.updateFarmPlot(plotIndex, {
                state: 'ready',
                plantedAt: Date.now(),
                growthDuration: effectiveGrowthTime,
                cropType: cropType,
                cropId: cropId.toUpperCase(),
                cropData: cropData
            });
        } else {
            // Update plot state with normal growth
            gameState.updateFarmPlot(plotIndex, {
                state: 'growing',
                plantedAt: Date.now(),
                growthDuration: effectiveGrowthTime,
                cropType: cropType,
                cropId: cropId.toUpperCase(),
                cropData: cropData
            });
        }

        return { success: true, instantGrowth };
    }

    /**
     * Check plot progress and update state if ready
     * Returns progress (0-1) or null if not growing
     */
    checkPlotProgress(plotIndex) {
        const plot = gameState.getFarmPlot(plotIndex);
        if (!plot || plot.state !== 'growing') {
            return null;
        }

        const elapsed = Math.max(0, Date.now() - plot.plantedAt);
        const progress = Math.min(elapsed / plot.growthDuration, 1);

        if (progress >= 1 && plot.state === 'growing') {
            // Mark as ready
            gameState.updateFarmPlot(plotIndex, { state: 'ready' });

            // Handle auto-harvest if enabled
            const farmSettings = gameState.getFarmSettings();
            if (farmSettings.autoHarvestEnabled && this.isAutoHarvestUnlocked()) {
                this.harvestPlot(plotIndex, true);
            } else {
                logger.game(`${plot.cropData.name} is ready to harvest!`);
            }
        }

        return progress;
    }

    /**
     * Calculate yield multiplier based on crop type and talents
     */
    getYieldMultiplier(cropType) {
        let multiplier = 1.0;

        // Base loot bonus from talents
        const lootBonus = getTalentLootBonus(SKILLS.FARMING);
        if (lootBonus > 0) {
            multiplier += lootBonus;
        }

        // Abundant Fields capstone (+40% all yields)
        const abundantBonus = getTalentFlatBonus(SKILLS.FARMING, 'abundantYieldBonus');
        if (abundantBonus > 0) {
            multiplier += abundantBonus;
        }

        // Type-specific bonuses
        if (cropType === 'herb') {
            const herbBonus = getTalentFlatBonus(SKILLS.FARMING, 'herbYieldBonus');
            if (herbBonus > 0) {
                multiplier += herbBonus;
            }
        } else if (cropType === 'tree') {
            const treeBonus = getTalentFlatBonus(SKILLS.FARMING, 'treeYieldBonus');
            if (treeBonus > 0) {
                multiplier += treeBonus;
            }
        } else if (cropType === 'crop') {
            const cropBonus = getTalentFlatBonus(SKILLS.FARMING, 'cropYieldBonus');
            if (cropBonus > 0) {
                multiplier += cropBonus;
            }
        }

        return multiplier;
    }

    /**
     * Generate random drops for special crops like Eldritch Sapling
     * @param {Object} randomDrops - Configuration with pools and quantities
     * @param {number} multiplier - Yield multiplier to apply
     * @returns {Object} Map of itemId -> quantity
     */
    generateRandomDrops(randomDrops, multiplier) {
        const results = {};

        // Process each drop category (logs, seeds, etc.)
        for (const [category, config] of Object.entries(randomDrops)) {
            // Calculate quantity with randomness and multiplier
            const baseQty = Math.floor(Math.random() * (config.max - config.min + 1)) + config.min;
            const totalQty = Math.floor(baseQty * multiplier);

            // Randomly select items from the pool
            for (let i = 0; i < totalQty; i++) {
                const itemId = config.pool[Math.floor(Math.random() * config.pool.length)];
                results[itemId] = (results[itemId] || 0) + 1;
            }
        }

        return results;
    }

    /**
     * Harvest a specific plot
     */
    harvestPlot(plotIndex, isAutoHarvest = false) {
        const plot = gameState.getFarmPlot(plotIndex);
        if (!plot || plot.state !== 'ready') {
            return { success: false, reason: 'Nothing to harvest' };
        }

        const cropData = plot.cropData;
        const cropType = plot.cropType;
        const farmSettings = gameState.getFarmSettings();

        // Get loot multiplier from highest owned tool
        const toolLootMultiplier = this.getLootMultiplier();

        // Get yield multiplier from talents
        const talentYieldMultiplier = this.getYieldMultiplier(cropType);

        // Combined multiplier
        const totalMultiplier = toolLootMultiplier * talentYieldMultiplier;

        // Award XP (with Fertile Grove bonus for tree harvests)
        let effectiveXP = cropData.xp;
        if (cropType === 'tree') {
            const farmingXPBonus = getTalentFlatBonus(SKILLS.WOODCUTTING, 'farmingXPBonus');
            if (farmingXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + farmingXPBonus));
        }
        const xpResult = addXP(SKILLS.FARMING, effectiveXP, cropData.id);

        // Helper function to add items to appropriate destination
        const addHarvestItem = (itemId, quantity) => {
            // Track for bounty contracts
            bountySkill.trackItemProgress(itemId, quantity);

            if (isAutoHarvest) {
                const destination = farmSettings.autoHarvestDestination;

                if (destination === 'bloomhold' && gameState.isBloomholdUnlocked() && isBloomholdEligible(itemId)) {
                    const bhResult = gameState.addBloomholdItem(itemId, quantity);
                    if (bhResult.success && bhResult.added > 0) {
                        // Award bonus Architecting XP from Realm Steward talent for depositing
                        architectingSkill.awardBloomholdXP();
                    }
                    if (!bhResult.success || bhResult.added < quantity) {
                        const remainder = quantity - (bhResult.added || 0);
                        gameState.addBankItem(itemId, remainder);
                    }
                } else if (destination === 'bank') {
                    gameState.addBankItem(itemId, quantity);
                } else {
                    const result = gameState.addItem(itemId, quantity);
                    if (result.success) {
                        showItemGain(itemId, quantity);
                    } else {
                        gameState.addBankItem(itemId, quantity);
                    }
                }
            } else {
                const result = gameState.addItem(itemId, quantity);
                if (result.success) {
                    showItemGain(itemId, quantity);
                } else {
                    gameState.addBankItem(itemId, quantity);
                }
            }
        };

        // Handle random drops (e.g., Eldritch Sapling)
        if (cropData.randomDrops) {
            const randomDropResults = this.generateRandomDrops(cropData.randomDrops, totalMultiplier);
            for (const [itemId, quantity] of Object.entries(randomDropResults)) {
                addHarvestItem(itemId, quantity);
            }
        }

        // Add standard drops with multipliers
        for (const drop of cropData.drops) {
            const boostedQuantity = Math.floor(drop.quantity * totalMultiplier);
            addHarvestItem(drop.itemId, boostedQuantity);
        }

        // Check for secondHarvest proc (Abundant Fields path)
        const secondHarvest = rollTalentProc(SKILLS.FARMING, 'secondHarvest');
        if (secondHarvest) {
            // Handle random drops for Second Harvest
            if (cropData.randomDrops) {
                const bonusRandomDrops = this.generateRandomDrops(cropData.randomDrops, totalMultiplier);
                for (const [itemId, quantity] of Object.entries(bonusRandomDrops)) {
                    addHarvestItem(itemId, quantity);
                }
            }
            // Handle standard drops
            for (const drop of cropData.drops) {
                const boostedQuantity = Math.floor(drop.quantity * totalMultiplier);
                addHarvestItem(drop.itemId, boostedQuantity);
            }
            // Record proc for primary drop
            const primaryDrop = cropData.drops[0];
            if (primaryDrop) {
                recordTalentProc(SKILLS.FARMING, 'Second Harvest', null, null, { itemId: primaryDrop.itemId, quantity: Math.floor(primaryDrop.quantity * totalMultiplier) });
            }
        }

        // Check for seedReclaim proc (return seed)
        const seedReclaim = rollTalentProc(SKILLS.FARMING, 'seedReclaim');
        if (seedReclaim && cropData.seedId) {
            addHarvestItem(cropData.seedId, cropData.seedCost || 1);
            recordTalentProc(SKILLS.FARMING, 'Seed Reclamation', null, null, { itemId: cropData.seedId, quantity: cropData.seedCost || 1 });
        }

        // Check for bonusSeedDrop proc (Abundant Fields capstone)
        const bonusSeed = rollTalentProc(SKILLS.FARMING, 'bonusSeedDrop');
        if (bonusSeed && cropData.seedId) {
            addHarvestItem(cropData.seedId, 1);
            recordTalentProc(SKILLS.FARMING, 'Abundant Fields', null, null, { itemId: cropData.seedId, quantity: 1 });
        }

        logger.game(`Harvested ${cropData.name}! +${cropData.xp} XP (${totalMultiplier.toFixed(2)}x yield)`);

        // Determine what to replant based on auto-plant mode
        let replantCropType = cropType;
        let replantCropId = plot.cropId;
        let replantSeedId = cropData.seedId;
        let replantSeedCost = cropData.seedCost || 1;

        // If mode is 'specific', override with the user's chosen seed
        if (farmSettings.autoPlantMode === 'specific' && farmSettings.autoPlantCropType && farmSettings.autoPlantCropId) {
            const specificData = this.getCropData(farmSettings.autoPlantCropType, farmSettings.autoPlantCropId);
            if (specificData) {
                replantCropType = farmSettings.autoPlantCropType;
                replantCropId = farmSettings.autoPlantCropId;
                replantSeedId = specificData.seedId;
                replantSeedCost = specificData.seedCost || 1;
            }
        }

        // Clear the plot
        this.clearPlot(plotIndex);

        // Check for autoReplant (Eternal Garden capstone) - also check toggle setting
        const hasAutoReplant = getTalentFlatBonus(SKILLS.FARMING, 'autoReplant') > 0;
        if (hasAutoReplant && farmSettings.autoPlantEnabled && replantSeedId) {
            // Check if we have seeds to replant
            const inventorySeeds = gameState.getItemCount(replantSeedId);
            const bloomholdSeeds = gameState.isBloomholdUnlocked() ? gameState.getBloomholdItemCount(replantSeedId) : 0;
            const totalSeeds = inventorySeeds + bloomholdSeeds;

            if (totalSeeds >= replantSeedCost) {
                const replantResult = this.plantInPlot(plotIndex, replantCropType, replantCropId);
                if (replantResult.success) {
                    const replantData = this.getCropData(replantCropType, replantCropId);
                    logger.game(`Eternal Garden! Auto-replanted ${replantData?.name || cropData.name}`);
                }
            }
        }

        this.onHarvestComplete(cropData, xpResult);

        return { success: true, xpResult, cropData, secondHarvest, seedReclaim, bonusSeed };
    }

    /**
     * Clear a plot (reset to empty)
     */
    clearPlot(plotIndex) {
        gameState.updateFarmPlot(plotIndex, {
            state: 'empty',
            plantedAt: null,
            growthDuration: null,
            cropType: null,
            cropId: null,
            cropData: null
        });
    }

    /**
     * Remove a growing or ready plant from a plot (seeds are lost)
     */
    removePlant(plotIndex) {
        const plot = gameState.getFarmPlot(plotIndex);
        if (!plot) {
            return { success: false, reason: 'Invalid plot' };
        }

        if (plot.state === 'empty') {
            return { success: false, reason: 'Plot is already empty' };
        }

        const cropName = plot.cropData?.name || 'crop';
        this.clearPlot(plotIndex);
        logger.game(`Removed ${cropName} from plot ${plotIndex + 1}`);

        return { success: true };
    }

    /**
     * Get time remaining for a growing plot (in ms)
     */
    getTimeRemaining(plotIndex) {
        const plot = gameState.getFarmPlot(plotIndex);
        if (!plot || plot.state !== 'growing') {
            return null;
        }

        const elapsed = Math.max(0, Date.now() - plot.plantedAt);
        return Math.max(0, plot.growthDuration - elapsed);
    }

    /**
     * Get all plots that are ready to harvest
     */
    getReadyPlots() {
        const plots = gameState.getFarmPlots();
        return plots.filter(plot => plot.state === 'ready');
    }

    /**
     * Harvest all ready plots
     */
    harvestAll() {
        const readyPlots = this.getReadyPlots();
        const results = [];

        for (const plot of readyPlots) {
            const result = this.harvestPlot(plot.id);
            results.push(result);
        }

        return results;
    }

    /**
     * Get all empty and unlocked plots
     */
    getEmptyPlots() {
        const plots = gameState.getFarmPlots();
        return plots.filter((plot, index) =>
            plot.state === 'empty' && this.isPlotUnlocked(index)
        );
    }

    /**
     * Plant a crop in all available empty plots
     * Plants as many as possible based on seed availability
     * @param {string} cropType - 'crop', 'herb', or 'tree'
     * @param {string} cropId - The crop ID (e.g., 'POTATO', 'GREENLEAF')
     * @returns {{ success: boolean, planted: number, reason?: string }}
     */
    plantAll(cropType, cropId) {
        const emptyPlots = this.getEmptyPlots();
        if (emptyPlots.length === 0) {
            return { success: false, planted: 0, reason: 'No empty plots available' };
        }

        // Get crop data to check seed cost
        const canPlantResult = this.canPlant(cropType, cropId);
        if (!canPlantResult.success) {
            return { success: false, planted: 0, reason: canPlantResult.reason };
        }

        const cropData = canPlantResult.cropData;
        const seedCost = cropData.seedCost;

        // Calculate how many we can plant based on seeds available
        const inventoryCount = gameState.getItemCount(cropData.seedId);
        const bloomholdCount = gameState.isBloomholdUnlocked() ? gameState.getBloomholdItemCount(cropData.seedId) : 0;
        const totalSeeds = inventoryCount + bloomholdCount;
        const maxPlantable = Math.floor(totalSeeds / seedCost);

        if (maxPlantable === 0) {
            return { success: false, planted: 0, reason: `Not enough seeds (need ${seedCost} per plot)` };
        }

        // Plant in as many plots as we can
        const plotsToPlant = Math.min(emptyPlots.length, maxPlantable);
        let planted = 0;

        for (let i = 0; i < plotsToPlant; i++) {
            const plot = emptyPlots[i];
            const result = this.plantInPlot(plot.id, cropType, cropId);
            if (result.success) {
                planted++;
            }
        }

        if (planted === 0) {
            return { success: false, planted: 0, reason: 'Failed to plant in any plots' };
        }

        logger.game(`Planted ${cropData.name} in ${planted} plots`);
        return { success: true, planted };
    }

    // ==================== TOOL UPGRADES ====================

    /**
     * Purchase or equip a tool
     */
    upgradeTool(toolId) {
        const tool = Object.values(TOOLS).find(t => t.id === toolId);
        if (!tool) return { success: false, reason: 'Invalid tool' };

        const skill = gameState.getSkill(SKILLS.FARMING);
        if (skill.level < tool.level) {
            return { success: false, reason: `Requires level ${tool.level} Farming` };
        }

        // Check if already owned
        if (gameState.hasPurchasedTool(SKILLS.FARMING, toolId)) {
            // Just equip it
            gameState.equipTool(SKILLS.FARMING, toolId);
            return { success: true, action: 'equipped' };
        }

        // Try to purchase
        const purchaseResult = gameState.purchaseTool(SKILLS.FARMING, toolId, tool.price);
        if (!purchaseResult.success) {
            return purchaseResult;
        }

        // Auto-equip after purchase
        gameState.equipTool(SKILLS.FARMING, toolId);
        return { success: true, action: 'purchased' };
    }

    // ==================== CALLBACKS ====================

    onRakingComplete(xpResult) {
        if (xpResult.leveledUp) {
            logger.game(`Farming level up! Now level ${xpResult.newLevel}`);
        }
    }

    onHarvestComplete(cropData, xpResult) {
        if (xpResult.leveledUp) {
            logger.game(`Farming level up! Now level ${xpResult.newLevel}`);
        }
    }
}

export const farmingSkill = new FarmingSkill();
export default farmingSkill;
